// src/encounter/complaint-map/phrases-first.ts
//
// PROD-2947 Part 2 · frame v2 · the 6 Oct contract G8: "Phrases are offered by section and chief complaint in the order
// of starter-map.json. They are suggested first and never auto-inserted." This module answers "which triggers go first
// in a line that answers to these editors, for this visit's complaints?" and reorders a phrase list by it. Nothing is
// inserted, hidden or added: a phrase that is not in the clinician's library is simply not there to move.
//
// The order, for the FIRST complaint with a complaint-map row (by catalogue code; by exact name when the code is empty):
//   1. the library's own starter map (library-starters.json, a copy kept equal to scripts/templates/library by the test)
//      for the presentation (pcp-adult) or visit reason (nurse) the row links (`phraseLinks`), in the starter map's
//      order — sections cc, hpi, ros, pe, labs, a, p for the physician; S, O, P for the nurse — keeping the phrases
//      written for one of the line's editors;
//   2. then the complaint map's own `phrasesFirst` for those editors (its one-phrase-per-system order, PROD-2789), for
//      what step 1 did not already list.
// Who is offered which library (the map's `phraseLibraries` note): pcp-adult-v1 to a PHYSICIAN, the nurse library to a
// NURSE, each for a patient 18 or older; a row's `phraseGates` raise that age (the nurse's falls visit from 65). Any other
// author, an unknown age or a child → nothing is moved.
import type { AuthorRole } from '@scripsteam/scrips-signal-ds';
import type { NoteMacro } from '../lib/smart-phrase';
import { ageOnDate } from '../lib/chart-population';
import starters from './library-starters.json';
import { rowFor, type ChiefComplaintRef, type ComplaintMapRow } from './system-order-for';

const PRESENTATIONS = starters['pcp-adult-v1'].presentations as Readonly<Record<string, readonly string[]>>;
const VISIT_REASONS = starters['phrase-library-nurse-v1'].visitReasons as Readonly<Record<string, readonly string[]>>;
const PHRASE_EDITORS = starters.editors as Readonly<Record<string, readonly string[]>>;

export const PHYSICIAN_LIBRARY = 'pcp-adult-v1';
export const NURSE_LIBRARY = 'phrase-library-nurse-v1';
const ADULT_YEARS = 18;

/** The library whose order this author is offered, or none. */
export function libraryFor(author: AuthorRole): string | undefined {
  if (author === 'physician') return PHYSICIAN_LIBRARY;
  if (author === 'nurse') return NURSE_LIBRARY;
  return undefined;
}

/** The starter-map list the row links for this library (the pcp presentation, or the nurse visit reason). */
function starterList(library: string, row: ComplaintMapRow): readonly string[] {
  const links = row.phraseLinks;
  if (library === PHYSICIAN_LIBRARY) return (links?.pcpPresentation && PRESENTATIONS[links.pcpPresentation]) || [];
  if (library === NURSE_LIBRARY) return (links?.nurseVisitReason && VISIT_REASONS[links.nurseVisitReason]) || [];
  return [];
}

/** The triggers (without their dot) to offer first in a line answering to `editors`, starter-map order first. */
export function phrasesFirstFor(
  editors: readonly string[],
  complaints: readonly ChiefComplaintRef[] | undefined,
  author: AuthorRole,
  ageYears: number | undefined,
): string[] {
  const library = libraryFor(author);
  if (!library || ageYears === undefined || ageYears < ADULT_YEARS) return [];
  for (const complaint of complaints ?? []) {
    const row = rowFor(complaint);
    if (!row) continue;
    const gate = row.phraseGates?.[library]?.minAgeYears;
    if (gate !== undefined && ageYears < gate) return [];
    const out: string[] = [];
    const add = (t: string) => {
      const trigger = t.replace(/^\./, '').trim().toLowerCase();
      if (trigger && !out.includes(trigger)) out.push(trigger);
    };
    // 1 · the starter map, in its order: the phrases written for one of this line's editors.
    for (const key of starterList(library, row)) if ((PHRASE_EDITORS[key] ?? []).some((e) => editors.includes(e))) add(key);
    // 2 · then the complaint map's own order for these editors.
    const byEditor = row.phrasesFirst?.[library];
    for (const editor of editors) for (const t of byEditor?.[editor] ?? []) add(t);
    return out;
  }
  return [];
}

/** The page's suggester (StructuredNotePhrases.suggestFor): the visit's complaints, the author, and the age on the
 *  visit date from the record's birth date (none known → nothing is moved). */
export function phraseSuggester(
  complaints: readonly ChiefComplaintRef[] | undefined,
  author: AuthorRole,
  birthDate: string | undefined,
  visitDate: string | undefined,
): (editors: readonly string[]) => string[] {
  const ageYears = birthDate ? ageOnDate(birthDate, visitDate) : undefined;
  return (editors) => phrasesFirstFor(editors, complaints, author, ageYears);
}

/** `macros` with the suggested ones first, in the suggested order; every other phrase keeps its place after them. */
export function suggestedFirst<M extends Pick<NoteMacro, 'trigger'>>(macros: readonly M[], triggers: readonly string[]): M[] {
  if (triggers.length === 0) return [...macros];
  const rank = (m: M) => {
    const i = triggers.indexOf(m.trigger.trim().toLowerCase());
    return i < 0 ? Number.MAX_SAFE_INTEGER : i;
  };
  return macros
    .map((m, i) => ({ m, i, r: rank(m) }))
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map((x) => x.m);
}
